import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leagueApi, tournamentApi, Tournament, League } from '../services/api-client';
import { MobileLayout } from '../components/layout/MobileLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { formatDate, getUserTimezone } from '../utils/dateTime';
import { ShareButton } from '../components/ShareButton';

type Step = 'select-tournament' | 'league-details' | 'success';

export function CreateLeaguePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('select-tournament');
  const [createdLeague, setCreatedLeague] = useState<League | null>(null);

  // League creation fields
  const [leagueName, setLeagueName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [budgetPerTeam, setBudgetPerTeam] = useState('67');
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [rosterLockTime, setRosterLockTime] = useState('');

  // UI state
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const data = await tournamentApi.list();
      const now = new Date();
      setTournaments(data.filter((t) => !t.endDate || new Date(t.endDate) >= now));
    } catch (err) {
      console.error('Failed to load tournaments:', err);
      setError('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTournament = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setStep('league-details');
    setError(null);

    // Pre-populate budget from tournament
    if (tournament.defaultBudget) {
      setBudgetPerTeam(tournament.defaultBudget.toString());
    }

    // Pre-populate roster lock time
    const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
    let defaultLockTime = oneHourFromNow;

    // Try using the tournament's default roster lock time first
    if (tournament.defaultRosterLockTime) {
      const tournamentDefault = new Date(tournament.defaultRosterLockTime);
      if (tournamentDefault > oneHourFromNow) {
        defaultLockTime = tournamentDefault;
      }
    }

    // If we're still using oneHourFromNow (no defaultRosterLockTime, or it was in the past),
    // fall back to checking tournament start date
    if (defaultLockTime === oneHourFromNow && tournament.startDate) {
      // Parse the start date and set time to 9:00 AM local time
      const tournamentStart = new Date(tournament.startDate + 'T09:00:00');
      if (tournamentStart > oneHourFromNow) {
        defaultLockTime = tournamentStart;
      }
    }

    // Format to datetime-local input format in local time (YYYY-MM-DDThh:mm)
    const year = defaultLockTime.getFullYear();
    const month = String(defaultLockTime.getMonth() + 1).padStart(2, '0');
    const day = String(defaultLockTime.getDate()).padStart(2, '0');
    const hours = String(defaultLockTime.getHours()).padStart(2, '0');
    const minutes = String(defaultLockTime.getMinutes()).padStart(2, '0');
    const formattedTime = `${year}-${month}-${day}T${hours}:${minutes}`;

    setRosterLockTime(formattedTime);
  };

  const handleCreateLeague = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTournament) {
      setError('No tournament selected');
      return;
    }

    // Validate roster lock time is in the future
    if (rosterLockTime) {
      const lockTime = new Date(rosterLockTime);
      const now = new Date();
      if (lockTime <= now) {
        setError('Roster lock time must be in the future');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const league = await leagueApi.create({
        name: leagueName,
        teamName: teamName || undefined,
        budgetPerTeam: parseInt(budgetPerTeam),
        tournamentId: selectedTournament.id,
        rosterLockTime: rosterLockTime ? new Date(rosterLockTime).toISOString() : undefined,
      });

      setCreatedLeague(league);
      setStep('success');
    } catch (err: any) {
      console.error('Failed to create league:', err);
      setError(err instanceof Error ? err.message : 'Failed to create league');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileLayout>
      <PageHeader
        title={
          step === 'select-tournament'
            ? 'Select Tournament'
            : step === 'success'
              ? 'League Created!'
              : 'League Details'
        }
        showBack={step !== 'success'}
      />

      <div className="p-4">
        {step === 'select-tournament' && (
          <>
            {loading && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
                {error}
              </div>
            )}

            {!loading && tournaments.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p className="mb-2">No tournaments available.</p>
                <p className="text-sm">More coming soon.</p>
              </div>
            )}

            {!loading && tournaments.length > 0 && (
              <div className="space-y-2">
                {tournaments.map((tournament) => (
                  <button
                    key={tournament.id}
                    onClick={() => handleSelectTournament(tournament)}
                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition-colors"
                  >
                    <div className="font-medium">{tournament.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {tournament.location && <span>{tournament.location} • </span>}
                      {formatDate(tournament.startDate)} - {formatDate(tournament.endDate)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Status: {tournament.status}</div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {step === 'success' && createdLeague && selectedTournament && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">🎉</div>
              <h2 className="text-xl font-bold text-green-900 mb-1">{createdLeague.name}</h2>
              <p className="text-sm text-green-700">
                Your league is live! Invite friends to compete.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="text-sm font-medium text-gray-700">Share your league:</div>
              <ShareButton
                text={`I just created a ${selectedTournament.name} fantasy wrestling league on #TakedownLeague. Join my league and prove you know wrestling! #MarchMATness`}
                hashtags={selectedTournament.hashtags}
                label="Share league"
                className="w-full justify-center"
              />
              {createdLeague.inviteCode && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">Invite code:</div>
                  <div className="font-mono font-semibold text-gray-900 text-lg tracking-widest">
                    {createdLeague.inviteCode}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate(`/leagues/${createdLeague.id}`)}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-md font-medium hover:shadow-lg transition-shadow"
            >
              View My League
            </button>
          </div>
        )}

        {step === 'league-details' && selectedTournament && (
          <div>
            {/* Selected Tournament Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="text-sm text-blue-800 font-medium mb-1">Selected Tournament</div>
              <div className="font-semibold">{selectedTournament.name}</div>
              {selectedTournament.location && (
                <div className="text-sm text-gray-600 mt-1">{selectedTournament.location}</div>
              )}
              <div className="text-sm text-gray-600 mt-1">
                {formatDate(selectedTournament.startDate)} -{' '}
                {formatDate(selectedTournament.endDate)}
              </div>
            </div>

            {/* League Creation Form */}
            <form onSubmit={handleCreateLeague} className="space-y-4">
              <div>
                <label
                  htmlFor="leagueName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  League Name
                </label>
                <input
                  type="text"
                  id="leagueName"
                  value={leagueName}
                  onChange={(e) => setLeagueName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Smith Family League"
                />
              </div>

              <div>
                <label htmlFor="teamName" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Team Name
                </label>
                <input
                  type="text"
                  id="teamName"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Team Smith"
                />
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                  Budget Per Team
                </label>
                <input
                  type="number"
                  id="budget"
                  value={budgetPerTeam}
                  onChange={(e) => setBudgetPerTeam(e.target.value)}
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="100"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Draft credits for selecting wrestlers. Higher seeded wrestlers cost more
                  {selectedTournament.placePoints.length > 0 &&
                    ` (1st seed = ${selectedTournament.placePoints[0]} credits)`}
                  .
                </p>
              </div>

              <div>
                <label
                  htmlFor="rosterLockTime"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Roster Lock Time ({getUserTimezone()})
                </label>
                <input
                  type="datetime-local"
                  id="rosterLockTime"
                  value={rosterLockTime}
                  onChange={(e) => setRosterLockTime(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Rosters will automatically lock at this time (in your local timezone). Must be at
                  least 1 hour in the future.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating League...' : 'Create League'}
              </button>
            </form>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}
